import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Truck, Package, Calendar, Eye } from "lucide-react";
import { loadingPlansApi, palletTypesApi } from "@/services/loading-plan-api";
import { routesApi, vehiclesApi } from "@/services/api";
import type {
  LoadingPlanResponse,
  PalletType,
  GenerateLoadingPlanRequest,
} from "@/types/loading-plan";
import {
  DateFilter,
  type DateFilterValue,
  type DateRange,
} from "../orders/components/DateFilter";

const getDateRange = (
  filter: DateFilterValue,
  customRange?: DateRange,
): DateRange | null => {
  const today = new Date();
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  switch (filter) {
    case "today":
      return { from: fmt(today), to: fmt(today) };
    case "yesterday": {
      const d = new Date(today); d.setDate(d.getDate() - 1);
      return { from: fmt(d), to: fmt(d) };
    }
    case "tomorrow": {
      const d = new Date(today); d.setDate(d.getDate() + 1);
      return { from: fmt(d), to: fmt(d) };
    }
    case "day-before-yesterday": {
      const d = new Date(today); d.setDate(d.getDate() - 2);
      return { from: fmt(d), to: fmt(d) };
    }
    case "day-after-tomorrow": {
      const d = new Date(today); d.setDate(d.getDate() + 2);
      return { from: fmt(d), to: fmt(d) };
    }
    case "this-week": {
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay() + 1);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return { from: fmt(start), to: fmt(end) };
    }
    case "this-month": {
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`, to: fmt(end) };
    }
    case "custom":
      return customRange || null;
    default:
      return null;
  }
};

export function LoadingPlansList() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<LoadingPlanResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [routes, setRoutes] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [palletTypes, setPalletTypes] = useState<PalletType[]>([]);

  // Filtros de fecha
  const [dateFilter, setDateFilter] = useState<DateFilterValue>("today");
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();

  const [formData, setFormData] = useState<GenerateLoadingPlanRequest>({
    routeId: "",
    plannedDate: new Date().toISOString().split("T")[0],
    vehicleId: undefined,
    palletTypeId: undefined,
  });

  useEffect(() => {
    loadData();
  }, [dateFilter, customDateRange]);

  const handleDateFilterChange = (value: DateFilterValue, customRange?: DateRange) => {
    setDateFilter(value);
    setCustomDateRange(customRange);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const range = getDateRange(dateFilter, customDateRange);
      const [routesData, vehiclesData, plansData] = await Promise.all([
        routesApi.getAll(),
        vehiclesApi.getAll(),
        loadingPlansApi.getAll(
          range ? { startDate: range.from, endDate: range.to } : undefined,
        ),
      ]);
      setRoutes(routesData);
      setVehicles(vehiclesData);
      setPlans(plansData);

      try {
        const palletTypesData = await palletTypesApi.getAll();
        setPalletTypes(palletTypesData);
      } catch (err) {
        console.warn("Pallet types not available yet:", err);
        setPalletTypes([]);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      alert(
        "Error al cargar datos. Por favor, verifica que las rutas y vehículos estén configurados.",
      );
    } finally {
      setLoading(false);
    }
  };


  const handleGenerate = async () => {
    try {
      setLoading(true);
      const plan = await loadingPlansApi.generate(formData);
      setGenerateDialogOpen(false);
      // Reload the plans list to show the new plan
      await loadData();
      navigate(`/logistica/planes-carga/${plan.id}`);
    } catch (err) {
      console.error("Error generating plan:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Error desconocido";
      alert(
        `Error al generar el plan de carga: ${errorMessage}\n\nAsegúrate de que:\n- La ruta tiene pedidos para la fecha seleccionada\n- Los productos tienen envases configurados\n- Hay un vehículo disponible`,
      );
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    Draft: "bg-gray-500",
    Calculated: "bg-blue-500",
    Approved: "bg-green-500",
    InProgress: "bg-yellow-500",
    Completed: "bg-purple-500",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Planes de Carga</h1>
          <p className="text-muted-foreground">
            Gestiona los planes de carga optimizados para tus rutas
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateFilter
            value={dateFilter}
            customRange={customDateRange}
            onChange={handleDateFilterChange}
          />
          <Button onClick={() => setGenerateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Generar Plan
          </Button>
        </div>
      </div>

      {plans.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">
              {loading ? "Cargando..." : "No hay planes de carga"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {loading
                ? ""
                : (() => {
                    const range = getDateRange(dateFilter, customDateRange);
                    if (!range) return "No hay planes de carga.";
                    return range.from === range.to
                      ? `No se encontraron planes para el ${new Date(range.from + "T00:00:00").toLocaleDateString()}`
                      : `No se encontraron planes entre ${new Date(range.from + "T00:00:00").toLocaleDateString()} y ${new Date(range.to + "T00:00:00").toLocaleDateString()}`;
                  })()}
            </p>
            <Button onClick={() => setGenerateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Generar Plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/logistica/planes-carga/${plan.id}`)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{plan.routeName}</CardTitle>
                  <Badge className={statusColors[plan.status]}>
                    {plan.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {new Date(plan.plannedDate).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Truck className="h-4 w-4" />
                  {plan.vehicleName}
                </div>
                <div className="grid grid-cols-3 gap-2 pt-2">
                  <div className="text-center">
                    <div className="text-2xl font-bold">
                      {plan.metrics.totalPallets}
                    </div>
                    <div className="text-xs text-muted-foreground">Palets</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">
                      {plan.metrics.totalWeightKg.toFixed(0)}
                    </div>
                    <div className="text-xs text-muted-foreground">kg</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">
                      {plan.metrics.capacityUtilizationPercent.toFixed(0)}%
                    </div>
                    <div className="text-xs text-muted-foreground">Uso</div>
                  </div>
                </div>
                <Button variant="outline" className="w-full mt-2" size="sm">
                  <Eye className="h-4 w-4 mr-2" />
                  Ver Detalles
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Generate Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generar Plan de Carga</DialogTitle>
            <DialogDescription>
              Selecciona la ruta y fecha para generar un plan de carga
              optimizado
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="route">Ruta *</Label>
              <Select
                value={formData.routeId}
                onValueChange={(value) =>
                  setFormData({ ...formData, routeId: value })
                }
              >
                <SelectTrigger id="route">
                  <SelectValue placeholder="Selecciona una ruta" />
                </SelectTrigger>
                <SelectContent>
                  {routes.map((route) => (
                    <SelectItem key={route.id} value={route.id}>
                      {route.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date">Fecha *</Label>
              <Input
                id="date"
                type="date"
                value={formData.plannedDate}
                onChange={(e) =>
                  setFormData({ ...formData, plannedDate: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vehicle">Vehículo (opcional)</Label>
              <Select
                value={formData.vehicleId || "auto"}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    vehicleId: value === "auto" ? undefined : value,
                  })
                }
              >
                <SelectTrigger id="vehicle">
                  <SelectValue placeholder="Auto-seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-seleccionar</SelectItem>
                  {vehicles.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="palletType">Tipo de Palet (opcional)</Label>
              <Select
                value={formData.palletTypeId || "auto"}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    palletTypeId: value === "auto" ? undefined : value,
                  })
                }
              >
                <SelectTrigger id="palletType">
                  <SelectValue placeholder="Europalet por defecto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Europalet por defecto</SelectItem>
                  {palletTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name} ({type.lengthCm}×{type.widthCm} cm)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setGenerateDialogOpen(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={!formData.routeId || !formData.plannedDate || loading}
            >
              {loading ? "Generando..." : "Generar Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
